import { startTransition, useEffect, useRef, useState } from "react";

const DESKTOP_MQ = "(min-width: 1024px)";
const HERO_OUT_RATIO = 0.15;
const MORPH_START_RATIO = 0.72;
const MORPH_END_RATIO = 0.15;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const lerp = (start, end, progress) => start + (end - start) * progress;
const roundPx = (value) => Math.round(value * 10) / 10;

const getVisibleRatioFromRect = (rect) => {
    if (rect.height <= 0) return 1;
    const visibleHeight = Math.max(
        0,
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    );
    return clamp(visibleHeight / rect.height, 0, 1);
};

const setAnchorVisibility = (element, hidden) => {
    if (!element) return;
    element.style.visibility = hidden ? "hidden" : "";
};

const hideMorphElement = (element) => {
    if (!element) return;
    element.style.display = "none";
    element.style.position = "";
    element.style.left = "";
    element.style.top = "";
    element.style.width = "";
    element.style.height = "";
    element.style.transform = "";
    element.style.transformOrigin = "";
    element.style.willChange = "";
    element.style.pointerEvents = "";
    element.style.removeProperty("contain");
    element.style.removeProperty("backface-visibility");
    element.style.removeProperty("--event-card-progress");
    element.style.removeProperty("--event-card-image-height");
    element.style.removeProperty("--event-card-details-height");
};

const stylesAreEqual = (a, b) =>
    a.transform === b.transform &&
    a.width === b.width &&
    a.height === b.height &&
    a.progress === b.progress &&
    a.imageHeight === b.imageHeight &&
    a.detailsHeight === b.detailsHeight;

/**
 * Manual FLIP/shared-card animation for the desktop event card.
 *
 * The real hero and sticky cards remain mounted as layout anchors. A single
 * fixed visual card is translated/resized between their measured rectangles.
 * This avoids the old cross-fade where one card disappeared and another
 * appeared.
 */
export function useHeroStickyTransition({
    heroImageRef,
    heroCardRef,
    stickyCardRef,
    morphCardRef,
    enabled = true,
}) {
    const [heroImageOut, setHeroImageOut] = useState(false);
    const [syncedReveal, setSyncedReveal] = useState(false);
    const heroImageOutRef = useRef(false);
    const syncedRevealRef = useRef(false);

    useEffect(() => {
        const mq = window.matchMedia(DESKTOP_MQ);
        let rafId = null;
        let attachRetryId = null;
        let resizeObserver = null;
        let morphPrimed = false;
        let anchorsHidden = false;
        let lastStyles = null;

        const reset = () => {
            [heroCardRef.current, stickyCardRef.current].forEach((element) => {
                setAnchorVisibility(element, false);
            });
            hideMorphElement(morphCardRef.current);
            heroImageOutRef.current = false;
            syncedRevealRef.current = false;
            morphPrimed = false;
            anchorsHidden = false;
            lastStyles = null;
            startTransition(() => {
                setHeroImageOut(false);
                setSyncedReveal(false);
            });
        };

        const updateState = (progress, visibleRatio) => {
            const nextHeroOut = visibleRatio < HERO_OUT_RATIO;
            if (nextHeroOut !== heroImageOutRef.current) {
                heroImageOutRef.current = nextHeroOut;
                startTransition(() => {
                    setHeroImageOut(nextHeroOut);
                });
            }

            const nextSyncedReveal = progress > 0.02;
            if (nextSyncedReveal !== syncedRevealRef.current) {
                syncedRevealRef.current = nextSyncedReveal;
                startTransition(() => {
                    setSyncedReveal(nextSyncedReveal);
                });
            }
        };

        const primeMorphElement = (morphEl) => {
            if (morphPrimed) return;
            morphEl.style.display = "block";
            morphEl.style.position = "fixed";
            morphEl.style.left = "0";
            morphEl.style.top = "0";
            morphEl.style.transformOrigin = "top left";
            morphEl.style.willChange = "transform, width, height";
            morphEl.style.backfaceVisibility = "hidden";
            morphEl.style.pointerEvents = "auto";
            morphPrimed = true;
        };

        const applyMorph = () => {
            rafId = null;

            const heroImageEl = heroImageRef.current;
            const sourceEl = heroCardRef.current;
            const targetEl = stickyCardRef.current;
            const morphEl = morphCardRef.current;

            if (!heroImageEl || !sourceEl || !targetEl || !morphEl) {
                attachRetryId = requestAnimationFrame(applyMorph);
                return;
            }

            if (attachRetryId !== null) {
                cancelAnimationFrame(attachRetryId);
                attachRetryId = null;
            }

            // Batch layout reads before any writes.
            const heroRect = heroImageEl.getBoundingClientRect();
            const sourceRect = sourceEl.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            const visibleRatio = getVisibleRatioFromRect(heroRect);
            const progress = clamp(
                (MORPH_START_RATIO - visibleRatio) /
                    (MORPH_START_RATIO - MORPH_END_RATIO),
                0,
                1
            );

            if (!anchorsHidden) {
                anchorsHidden = true;
                setAnchorVisibility(sourceEl, true);
                setAnchorVisibility(targetEl, true);
            }

            const left = roundPx(lerp(sourceRect.left, targetRect.left, progress));
            const top = roundPx(lerp(sourceRect.top, targetRect.top, progress));
            const width = roundPx(lerp(sourceRect.width, targetRect.width, progress));
            const height = roundPx(lerp(sourceRect.height, targetRect.height, progress));
            const imageHeight = roundPx((width / 2) * progress);
            const detailsHeight = roundPx(Math.max(0, sourceRect.height * (1 - progress)));

            const nextStyles = {
                transform: `translate3d(${left}px, ${top}px, 0)`,
                width: `${width}px`,
                height: `${height}px`,
                progress: String(progress),
                imageHeight: `${imageHeight}px`,
                detailsHeight: `${detailsHeight}px`,
            };

            primeMorphElement(morphEl);

            const stylesChanged = !lastStyles || !stylesAreEqual(lastStyles, nextStyles);
            if (stylesChanged) {
                morphEl.style.width = nextStyles.width;
                morphEl.style.height = nextStyles.height;
                morphEl.style.transform = nextStyles.transform;
                morphEl.style.setProperty("--event-card-progress", nextStyles.progress);
                morphEl.style.setProperty("--event-card-image-height", nextStyles.imageHeight);
                morphEl.style.setProperty("--event-card-details-height", nextStyles.detailsHeight);
                lastStyles = nextStyles;
            }

            updateState(progress, visibleRatio);

            // Keep updating at display refresh rate while the morph is active.
            if (progress > 0 && progress < 1 && stylesChanged) {
                rafId = requestAnimationFrame(applyMorph);
            }
        };

        const scheduleMorph = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(applyMorph);
        };

        const start = () => {
            if (!enabled || !mq.matches) {
                reset();
                return;
            }

            resizeObserver = new ResizeObserver(scheduleMorph);
            [
                heroImageRef.current,
                heroCardRef.current,
                stickyCardRef.current,
            ].forEach((element) => {
                if (element) resizeObserver.observe(element);
            });

            window.addEventListener("scroll", scheduleMorph, { passive: true });
            window.addEventListener("resize", scheduleMorph);
            scheduleMorph();
        };

        const stop = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            if (attachRetryId !== null) {
                cancelAnimationFrame(attachRetryId);
                attachRetryId = null;
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserver = null;
            }
            window.removeEventListener("scroll", scheduleMorph);
            window.removeEventListener("resize", scheduleMorph);
            reset();
        };

        const handleMqChange = () => {
            stop();
            start();
        };

        start();
        mq.addEventListener("change", handleMqChange);

        return () => {
            mq.removeEventListener("change", handleMqChange);
            stop();
        };
    }, [enabled, heroImageRef, heroCardRef, stickyCardRef, morphCardRef]);

    return { heroImageOut, syncedReveal };
}
